import React, { useEffect } from 'react';

import {
  createRootRoute, HeadContent, Scripts, useMatches,
} from '@tanstack/react-router';

import MainLayout from '../layouts/MainLayout';

import type { JSX } from 'react';

interface RootLayoutProps {
  // eslint-disable-next-line react/require-default-props
  children?: React.ReactNode;
}

export const RootLayout = ({ children }: RootLayoutProps): JSX.Element => {
  // Get the locale from the deepest route's context
  const matches = useMatches();
  const locale = matches.find((match) => match.context?.locale)?.context?.locale ?? 'nl';

  useEffect(() => {
    // Skip analytics in development
    if (process.env.NODE_ENV === 'development') {
      return;
    }

    // Load Ahrefs analytics with error handling
    const loadAnalytics = () => {
      try {
        const script = document.createElement('script');
        script.async = true;
        script.setAttribute('data-key', 'on8h+6S/T/zif21I12kr+g');
        script.src = 'https://analytics.ahrefs.com/analytics.js';
        script.onerror = () => {
          console.warn('Ahrefs analytics failed to load');
        };
        document.head.appendChild(script);
      } catch (error) {
        console.warn('Failed to initialize Ahrefs analytics:', error);
      }
    };

    loadAnalytics();
  }, []);

  return (
    <html lang={locale}>
      <head>
        <meta charSet="UTF-8" />
        <meta content="width=device-width, initial-scale=1.0" name="viewport" />

        <link href="/main.css" precedence="default" rel="stylesheet" />

        <link
          as="font"
          crossOrigin="anonymous"
          href="/fonts/Mona-Sans.var.woff2"
          rel="preload"
          type="font/woff2"
        />

        <HeadContent />
      </head>
      <body>
        <MainLayout>
          {children}
        </MainLayout>
        <Scripts />
      </body>
    </html>
  );
};

export const Route = createRootRoute({
  component: () => <RootLayout />,
  scripts: () => [
    {
      src: '/main.js',
      type: 'module',
      async: true,
    },
  ],
  head: () => ({
    links: [
      {
        rel: 'icon',
        type: 'ico',
        href: '/favicon.ico',
      },
      {
        rel: 'apple-touch-icon',
        href: '/favicon-180x180.png',
      },
      {
        rel: 'icon',
        type: 'image/png',
        sizes: '16x16',
        href: '/favicon-16x16.png',
      },
      {
        rel: 'icon',
        type: 'image/png',
        sizes: '32x32',
        href: '/favicon-32x32.png',
      },
      {
        rel: 'icon',
        type: 'image/png',
        sizes: '48x48',
        href: '/favicon-48x48.png',
      },
      {
        rel: 'icon',
        type: 'image/png',
        sizes: '180x180',
        href: '/favicon-180x180.png',
      },
      {
        rel: 'icon',
        type: 'image/png',
        sizes: '192x192',
        href: '/favicon-192x192.png',
      },
      {
        rel: 'icon',
        type: 'image/png',
        sizes: '512x512',
        href: '/favicon-512x512.png',
      },
    ],
  }),
});
